import moment from 'moment';
import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    FilteredValue,
    InputType,
} from '../../../../../../common/components/reactive-form-builder/interfaces/input.interface';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '../../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { EcorregimenModel } from '../../../../../../models/tareas/ecorregimen.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';

export class EcorregimenesFormDefinition {
    public readonly appName = environment.appName;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelaName = environment.features.parcelaName;
    public readonly hasFincas = environment.features.hasFincas;
    public readonly hasClients = environment.features.hasClients;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';
    public readonly showTareasFitoHoras = environment.features.showTareasFitoHoras;
    public readonly tareasHorasTotales = environment.features.tareasHorasTotales;

    public formType!: FormType;
    public model: Partial<EcorregimenModel> = {};
    public formFields!: Form<EcorregimenModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public tareasMaquinariaMalasHierbas: DropdownData<string> = list<string>(
        ['...', null],
        'Fangueo',
        'Pase de Ganchos 1',
        'Fresadora',
        'Nivelación',
        'Pase de Ganchos 2',
    );

    public tareasMaquinariaPalustre: DropdownData<string> = list<string>(
        ['...', null],
        'Abonar (Mecánica)',
        'Fangueo / Fresadora (Mecánica)',
        'Hacer Márgenes (Mecánica)',
        'Trailla (Mecánica)',
        'Láser (Mecánica)',
        'Cultivador (Mecánica)',
        'Rotovator (Mecánica)',
        'Multiapero (Mecánica)',
        'Abrir Boqueras (Escardaquímica)',
        'Bordoma (Escardaquímica)',
        'Transplantar (Escardaquímica)',
        'Escarda (Escardaquímica)',
        'Desbroce Cursos de Agua (Escardaquímica)',
        'Siega Orillas (Escardaquímica)',
        'Quemar Paja (Escardaquímica)',
    );

    public maquinariasMalasHierbas: DropdownData<string> = list<string>(['...', null], 'Fresadora', 'Gábia', 'Ganchos', 'Láser');
    public operacionesPaja: DropdownData<string> = list<string>(
        ['...', null],
        'Secado',
        'Quema',
        'Empaca',
        'Enterrado',
        'Retirada',
        'Otros',
    );

    public fieldNames: FormField<EcorregimenModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<EcorregimenModel>) {
        this.formType = +type;
        this.model = model;
        this.ensureDefaultDropdownValues();

        this.formFields = {
            name: 'tareas-ecorregimenes',
            header: `Datos de la tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    private getFormSections(): FormSection<EcorregimenModel>[] {
        const sections: FormSection<EcorregimenModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: 'Cultivo Arroz',
                width: 12,
                isVisible: true,
                fields: this.getArrozFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getPalustreFields(),
                order: 3,
            },
            {
                name: 'Control de Malas Hierbas en época de no cultivo',
                width: 12,
                isVisible: true,
                fields: this.getMalasHierbasFields(),
                order: 4,
            },
            {
                name: 'Actuación Rastrojo',
                width: 12,
                isVisible: this.appName !== 'avafitogest',
                fields: this.getRastrojoFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                vinculatedFields: undefined,
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => {
                            const fincaValue = it.value as unknown as FincasModel | null;

                            if (!this.hasClients || this.formType === FormType.EDITION || !this.userCanSeeClientes) {
                                return true;
                            }

                            if (this.client?.id) {
                                return String(fincaValue?.id_cliente ?? '') === String(this.client.id);
                            }

                            return true;
                        },
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true, isVisible: this.hasFincas }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : this.getDisplaySectorFieldName(),
                vinculatedFields: ['id_finca'],
                label: this.sectorName,
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => {
                            const sectorValue = it.value as unknown as SectorModel | null;

                            if (sectorValue === null) {
                                return true;
                            }

                            const finca = this.fincas.selected as FincasModel | null;

                            return String(sectorValue.id_finca ?? '') === String(finca?.id ?? '');
                        },
                    },
                },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                vinculatedFields: ['id_finca', 'id_sector'],
                label: this.parcelaName,
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterParcela(it),
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'fecha_entrada',
                vinculatedFields: undefined,
                label: 'Fecha Entrada Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 4,
            },
            {
                name: 'fecha_salida',
                vinculatedFields: undefined,
                label: 'Fecha Salida Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 5,
            },
            {
                name: 'fecha_seca_cultivo_agua',
                vinculatedFields: undefined,
                label: 'Fecha Seca Cultivo de Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 6,
            },
            {
                name: 'fecha_seca_arroz',
                vinculatedFields: undefined,
                label: 'Fecha Seca Arroz',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 7,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getArrozFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'fecha_siembra',
                vinculatedFields: undefined,
                label: 'Fecha Siembra',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'dosis_siembra',
                vinculatedFields: undefined,
                label: 'Dosis Siembra (kg/ha)',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'avafitogest' }),
                order: 2,
            },
            {
                name: 'fecha_recoleccion_arroz',
                vinculatedFields: undefined,
                label: 'Fecha Recolección',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'control_mecanico_vn',
                vinculatedFields: undefined,
                label: 'Control mecánico de la Vegetación Natural Palustre próxima a cursos de agua',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getPalustreFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'tareas_palustre',
                vinculatedFields: ['control_mecanico_vn'],
                label: 'Tareas Palustre',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tareasMaquinariaPalustre,
                    defaultValue: 'Hacer Márgenes (Mecánica)',
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => {
                    field.conditions.isVisible = this.isControlMecanicoVisible();
                },
                order: 1,
            },
            {
                name: 'fecha_paluestre',
                vinculatedFields: ['control_mecanico_vn'],
                label: 'Fecha Palustre',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => {
                    field.conditions.isVisible = this.isControlMecanicoVisible();
                },
                order: 2,
            },
            {
                name: 'id_maquinaria_palustre',
                vinculatedFields: ['control_mecanico_vn'],
                label: 'Maquinaria',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => {
                    field.conditions.isVisible = this.isControlMecanicoVisible();
                },
                order: 3,
            },
            {
                name: 'observaciones_palustre',
                vinculatedFields: ['control_mecanico_vn'],
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => {
                    field.conditions.isVisible = this.isControlMecanicoVisible();
                },
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getMalasHierbasFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'tareas_malashierbas',
                vinculatedFields: undefined,
                label: 'Tareas',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tareasMaquinariaMalasHierbas,
                    defaultValue: 'Fangueo',
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'fecha_malashierbas',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'id_maquinaria_malashierbas',
                vinculatedFields: undefined,
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.maquinariasMalasHierbas,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'observaciones_malashierbas',
                vinculatedFields: undefined,
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getRastrojoFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'fecha_recoleccion_rastrojo',
                vinculatedFields: undefined,
                label: 'Fecha Recolección',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 1,
            },
            {
                name: 'operacion_paja',
                vinculatedFields: undefined,
                label: 'Operación',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.operacionesPaja,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 2,
            },
            {
                name: 'fecha_operacion',
                vinculatedFields: undefined,
                label: 'Fecha Operación',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 3,
            },
            {
                name: 'hora_inicio',
                vinculatedFields: undefined,
                label: 'Hora Inicio Quema',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' && this.showTareasFitoHoras }),
                order: 4,
            },
            {
                name: 'hora_fin',
                vinculatedFields: undefined,
                label: 'Hora Fin Quema',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' && this.showTareasFitoHoras }),
                order: 5,
            },
            {
                name: 'horas_totales',
                vinculatedFields: ['hora_inicio', 'hora_fin'],
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({ isVisible: this.appName === 'agroleza' }),
                order: 6,
            },
        ];

        return this.registerVisibleFields(fields);
    }


    private getDisplaySectorFieldName(): string {
        return this.appName === 'avafitogest' ? 'sectores' : 'sector';
    }

    private getHiddenFields(): FormField<EcorregimenModel>[] {
        return [
            this.hiddenField('id', this.model.id),
            this.hiddenField('id_cliente', this.getInitialClientId()),
            this.hiddenField('id_usuario', this.getInitialUserId()),
            this.hiddenField('adjunto_guia', this.model.adjunto_guia),
            this.hiddenField('fecha_inicio', this.model.fecha_inicio),
            this.hiddenField('fecha_fin', this.model.fecha_fin),
            this.hiddenField('fecha_fin_inundacion_invernal', this.model.fecha_fin_inundacion_invernal),
            this.hiddenField('fecha_seca_inicio_intermitente', this.model.fecha_seca_inicio_intermitente),
            this.hiddenField('fecha_seca_fin_intermitente', this.model.fecha_seca_fin_intermitente),
            this.hiddenField('hora_inicio_intermitente', this.model.hora_inicio_intermitente),
            this.hiddenField('hora_fin_intermitente', this.model.hora_fin_intermitente),
        ];
    }

    private hiddenField(name: keyof EcorregimenModel & string, defaultValue: unknown): FormField<EcorregimenModel> {
        const input: FormField<EcorregimenModel>['input'] = {
            type: InputType.HIDDEN,
            isEditable: false,
        };

        if (defaultValue !== undefined) {
            input.defaultValue = defaultValue as string | number | boolean | Date | EcorregimenModel | null;
        }

        return {
            name,
            vinculatedFields: undefined,
            label: '',
            input,
            width: 1,
            conditions: this.conditions({ isVisible: true }),
            order: 1,
        };
    }

    private filterParcela(it: FilteredValue<EcorregimenModel>): boolean {
        const parcelaValue = it.value as unknown as ParcelasModel | null;

        if (parcelaValue === null) {
            return true;
        }

        if (parcelaValue.activo?.toString() !== '1') {
            return false;
        }

        const sectores = this.sectores.selected as SectorModel | SectorModel[] | null;
        const finca = this.fincas.selected as FincasModel | null;

        if (Array.isArray(sectores) && sectores.length > 0) {
            return sectores.some((sector: SectorModel) => String(sector?.id ?? '') === String(parcelaValue?.id_sector ?? ''));
        }

        if (sectores !== null && typeof sectores === 'object' && !Array.isArray(sectores)) {
            return String(parcelaValue.id_sector ?? '') === String(sectores.id ?? '');
        }

        return String(parcelaValue.id_finca ?? '') === String(finca?.id ?? '');
    }

    private registerVisibleFields(fields: FormField<EcorregimenModel>[]): FormField<EcorregimenModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private conditions(options: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: options.isRememberedOnDuplicate ?? true,
            isRequired: options.isRequired ?? false,
            isRetained: options.isRetained ?? false,
            hasUndoEnabled: options.hasUndoEnabled ?? false,
            isVisible: options.isVisible ?? true,
        };
    }

    private ensureDefaultDropdownValues(): void {
        if (!this.model.tareas_palustre) {
            this.model.tareas_palustre = 'Hacer Márgenes (Mecánica)';
        }

        if (!this.model.tareas_malashierbas) {
            this.model.tareas_malashierbas = 'Fangueo';
        }
    }

    private isControlMecanicoVisible(): boolean {
        return this.isTruthy(this.model.control_mecanico_vn);
    }

    private isTruthy(value: unknown): boolean {
        return value === true || value === 1 || value === '1' || value === 'true';
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid()) {
            console.error('Invalid time format. Expected HH:mm.', { startTime, endTime });
            return '';
        }

        if (end.isBefore(start)) {
            console.warn('End time is before start time.', { startTime, endTime });
            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private getInitialClientId(): string {
        if (this.user.tipo === 'comunero') {
            return this.user.id_cliente ?? '';
        }

        return String(this.model.id_cliente ?? this.client?.id ?? '');
    }

    private getInitialUserId(): string {
        return String(this.model.id_usuario ?? this.user.id ?? '');
    }
}
